import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useIntl } from "react-intl";
import VenueMapPicker from "./VenueMapPicker";

const emptyField = () => ({ id: null, name: "", file: null, preview: null });

// Typed coordinates: a finite number within ±limit, otherwise null (empty text is not an error).
const parseCoordinate = (text, limit) => {
  const value = Number(text);
  return text.trim() !== "" && Number.isFinite(value) && Math.abs(value) <= limit ? value : null;
};

const VenueFormDialog = ({ open, onClose, onSubmit, saving, venue, title }) => {
  const intl = useIntl();
  const isEdit = Boolean(venue?.id);
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [lat, setLat] = useState(null);
  const [lng, setLng] = useState(null);
  const [latText, setLatText] = useState("");
  const [lngText, setLngText] = useState("");
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [fields, setFields] = useState([emptyField()]);

  useEffect(() => {
    if (!open) return;
    setName(venue?.name || venue?.public_name || "");
    setAddress(venue?.address || "");
    setLat(venue?.lat ?? null);
    setLng(venue?.lng ?? null);
    setLatText(venue?.lat != null ? String(venue.lat) : "");
    setLngText(venue?.lng != null ? String(venue.lng) : "");
    setCoverFile(null);
    setCoverPreview(venue?.full_banner_url || null);
    const existingFields = (venue?.fields || venue?.spaces || []).filter((field) => !field.archived);
    setFields(
      existingFields.length
        ? existingFields.map((field) => ({
            id: field.id,
            name: field.name || "",
            file: null,
            preview: field.image_url || null,
          }))
        : [emptyField()],
    );
  }, [open, venue]);

  const canSave =
    Boolean(name.trim()) &&
    lat != null &&
    lng != null &&
    (isEdit || Boolean(coverFile));

  const missingItems = [
    !name.trim() && intl.formatMessage({ id: "gestion.venues.name" }),
    (lat == null || lng == null) && intl.formatMessage({ id: "gestion.venues.coordinates" }),
    !isEdit && !coverFile && intl.formatMessage({ id: "gestion.venues.cover" }),
  ].filter(Boolean);

  const latInvalid = latText.trim() !== "" && parseCoordinate(latText, 90) === null;
  const lngInvalid = lngText.trim() !== "" && parseCoordinate(lngText, 180) === null;

  const handleCoordinateChange = (text, limit, setText, setValue) => {
    setText(text);
    setValue(parseCoordinate(text, limit));
  };

  const handleSubmit = () => {
    if (!canSave || saving) return;
    const formData = new FormData();
    formData.append("name", name.trim());
    formData.append("address", address.trim());
    formData.append("lat", String(lat));
    formData.append("lng", String(lng));
    if (coverFile) formData.append("image", coverFile);
    fields.forEach((field, index) => {
      const fieldName = field.name.trim();
      if (!fieldName && !field.id) return;
      if (field.id) formData.append(`fields[${index}][id]`, String(field.id));
      formData.append(`fields[${index}][name]`, fieldName);
      if (field.file) formData.append(`fields[${index}][image]`, field.file);
    });
    onSubmit(formData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          required
          fullWidth
          sx={{ mt: 1 }}
          label={intl.formatMessage({ id: "gestion.venues.name" })}
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <TextField
          fullWidth
          sx={{ mt: 2 }}
          label={intl.formatMessage({ id: "gestion.venues.address" })}
          value={address}
          onChange={(event) => setAddress(event.target.value)}
        />
        {open && (
          <VenueMapPicker
            lat={lat}
            lng={lng}
            onChange={({ lat: nextLat, lng: nextLng, address: nextAddress }) => {
              setLat(nextLat);
              setLng(nextLng);
              setLatText(nextLat != null ? String(nextLat) : "");
              setLngText(nextLng != null ? String(nextLng) : "");
              if (nextAddress) setAddress(nextAddress);
            }}
          />
        )}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 2 }}>
          <TextField
            type="number"
            required
            fullWidth
            label={intl.formatMessage({ id: "gestion.venues.latitude" })}
            value={latText}
            error={latInvalid}
            helperText={
              latInvalid
                ? intl.formatMessage({ id: "gestion.venues.coordinatesInvalid" })
                : intl.formatMessage({ id: "gestion.venues.coordinatesHint" })
            }
            inputProps={{ step: "any" }}
            onChange={(event) => handleCoordinateChange(event.target.value, 90, setLatText, setLat)}
          />
          <TextField
            type="number"
            required
            fullWidth
            label={intl.formatMessage({ id: "gestion.venues.longitude" })}
            value={lngText}
            error={lngInvalid}
            helperText={lngInvalid ? intl.formatMessage({ id: "gestion.venues.coordinatesInvalid" }) : undefined}
            inputProps={{ step: "any" }}
            onChange={(event) => handleCoordinateChange(event.target.value, 180, setLngText, setLng)}
          />
        </Stack>
        <Typography variant="subtitle2" sx={{ mt: 2 }}>
          {intl.formatMessage({ id: "gestion.venues.cover" })}
          {isEdit ? "" : " *"}
        </Typography>
        <Button component="label" sx={{ mt: 1 }}>
          {intl.formatMessage({ id: "gestion.venues.choosePhoto" })}
          <input
            hidden
            type="file"
            accept="image/jpeg,image/png"
            onChange={(event) => {
              const file = event.target.files?.[0];
              setCoverFile(file || null);
              setCoverPreview(file ? URL.createObjectURL(file) : venue?.full_banner_url || null);
            }}
          />
        </Button>
        {coverPreview && (
          <Box component="img" src={coverPreview} alt="" sx={{ mt: 1, width: "100%", maxHeight: 160, objectFit: "cover", borderRadius: 1 }} />
        )}
        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
          {intl.formatMessage({ id: "gestion.venues.fields" })}
        </Typography>
        {fields.map((field, index) => (
          <Stack key={field.id || `new-${index}`} spacing={1} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label={intl.formatMessage({ id: "gestion.venues.fieldName" })}
              value={field.name}
              onChange={(event) => {
                const next = [...fields];
                next[index] = { ...next[index], name: event.target.value };
                setFields(next);
              }}
            />
            <Button component="label">
              {intl.formatMessage({ id: "gestion.venues.fieldPhoto" })}
              <input
                hidden
                type="file"
                accept="image/jpeg,image/png"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  const next = [...fields];
                  next[index] = {
                    ...next[index],
                    file: file || null,
                    preview: file ? URL.createObjectURL(file) : next[index].preview,
                  };
                  setFields(next);
                }}
              />
            </Button>
            {field.preview && (
              <Box
                component="img"
                src={field.preview}
                alt=""
                sx={{ width: 120, height: 80, objectFit: "cover", borderRadius: 1 }}
              />
            )}
          </Stack>
        ))}
        <Button sx={{ mt: 1 }} onClick={() => setFields((current) => [...current, emptyField()])}>
          {intl.formatMessage({ id: "gestion.venues.addField" })}
        </Button>
        {!canSave && !saving && missingItems.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
            {intl.formatMessage({ id: "gestion.venues.missingHint" }, { items: missingItems.join(", ") })}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{intl.formatMessage({ id: "league.planillero.cancel" })}</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving || !canSave}>
          {intl.formatMessage({ id: "league.planillero.save" })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VenueFormDialog;
